'use client';

/**
 * DraftNotice — the "restore your draft?" offer and the "Draft saved" line
 * for a form using `useFormDraft` (Complete_app §17.1 save-draft).
 *
 *     <DraftNotice draft={draft} />
 */
import { History } from 'lucide-react';

import { formatDateTime } from '@/lib/format';
import type { FormDraftHandle } from '@/lib/forms/useFormDraft';

import { Button } from './Button';
import { cn } from './cn';

export function DraftNotice<T>({
  draft,
  className,
}: {
  draft: FormDraftHandle<T>;
  className?: string;
}) {
  if (draft.pending) {
    return (
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-state-info-border bg-state-info-bg px-4 py-3 text-sm',
          className,
        )}
        role="status"
      >
        <span className="flex items-center gap-2 text-fg">
          <History aria-hidden className="h-4 w-4 shrink-0 text-state-info" />
          You have an unsaved draft from {formatDateTime(new Date(draft.pending.savedAt))}.
        </span>
        <span className="flex gap-2">
          <Button size="sm" type="button" variant="secondary" onClick={draft.discard}>
            Discard
          </Button>
          <Button size="sm" type="button" onClick={draft.restore}>
            Restore draft
          </Button>
        </span>
      </div>
    );
  }
  if (draft.savedAt) {
    return (
      <p aria-live="polite" className={cn('text-xs text-fg-muted', className)}>
        Draft saved on this device at {formatDateTime(new Date(draft.savedAt), { parts: { hour: 'numeric', minute: '2-digit' } })}
      </p>
    );
  }
  return null;
}
